import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { API_REFRESH_TOKEN, API_AUTH_URL } from 'constants/apiUrl'
import { JWT_TOKEN, REFRESH_TOKEN, USER_ROLE } from 'constants/localStorageKeys'
import { SIGN_IN_URL } from 'constants/routes'
import { history } from 'services/history'

export const requestInterceptorCallback = (config: AxiosRequestConfig) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`
  const userRole = localStorage.getItem(USER_ROLE)
  const url: string = config.url || ''
  if (userRole) {
    config.headers.SelectedRole =
      userRole === 'Funder' &&
      ((url.indexOf('/Syndicator') >= 0 && url.indexOf('/Syndicator/light') < 0) ||
        url.indexOf('/Representative/funders') >= 0 ||
        url.indexOf('/Representative/approvedFunders') >= 0 ||
        url.indexOf('/Storage/syndicatorDocuments') >= 0 ||
        url.indexOf('/Export/syndicator') >= 0)
        ? 'Syndicator'
        : userRole
  }
  return config
}

const publicInstance = axios.create({ baseURL: API_AUTH_URL })

const refreshTokenInstance = axios.create({ baseURL: API_AUTH_URL })

interface IRefreshLockManager {
  request: (name: string, callback: () => Promise<void>) => Promise<void>
}

const REFRESH_LOCK_NAME = 'refresh-access-token'

const runExclusivelyAcrossTabs = (callback: () => Promise<void>) => {
  const { locks } = navigator as Navigator & { locks?: IRefreshLockManager }
  return locks ? locks.request(REFRESH_LOCK_NAME, callback) : callback()
}

const isSessionRejected = (error: AxiosError) => {
  const status = error.response?.status
  return status !== undefined && status >= 400 && status < 500
}

const signOutAfterRejectedSession = () => {
  localStorage.removeItem(JWT_TOKEN)
  localStorage.removeItem(REFRESH_TOKEN)
  history.push(SIGN_IN_URL)
}

export const refreshAccessToken = (failedRequest: AxiosError) =>
  runExclusivelyAcrossTabs(async () => {
    const isTokenAlreadyRefreshed = failedRequest.config.headers?.Authorization !== `Bearer ${localStorage.getItem(JWT_TOKEN)}`
    const refreshToken = localStorage.getItem(REFRESH_TOKEN)
    if (isTokenAlreadyRefreshed || !refreshToken) return

    try {
      const response = await refreshTokenInstance.post(API_REFRESH_TOKEN, { refreshToken })
      localStorage.setItem(JWT_TOKEN, response.data.token)
      localStorage.setItem(REFRESH_TOKEN, response.data.refreshToken)
    } catch (error) {
      const isRefreshedByAnotherTab = localStorage.getItem(REFRESH_TOKEN) !== refreshToken
      if (isRefreshedByAnotherTab) return
      if (isSessionRejected(error)) signOutAfterRejectedSession()
      throw error
    }
  })

// eslint-disable-next-line
const requestPublicInterceptor = publicInstance.interceptors.request.use(
  function (config: AxiosRequestConfig) {
    config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`
    return config
  },
  function (error: AxiosError) {
    return Promise.reject(error)
  },
)

createAuthRefreshInterceptor(publicInstance, refreshAccessToken)

export { publicInstance }
